import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  getInstanceSettings,
  getEnabledApps,
  upsertInstanceSetting,
  deleteInstanceSetting,
} from 'api/admin';
import {
  storeEnabledApps,
  setEnabledAppsError,
} from 'redux/enabledApps/enabledAppsSlice';
import { useNotificationProvider } from 'hooks';
import useInstanceSettingsAccess from './useInstanceSettingsAccess';

export interface InstanceSetting {
  id: number;
  app: string;
  key: string;
  value: string;
  updatedAt: string;
  updatedBy: number | null;
}

interface NewSettingForm {
  app: string;
  key: string;
  value: string;
}

const EMPTY_FORM: NewSettingForm = { app: '', key: '', value: '' };

function useInstanceSettings() {
  const dispatch = useDispatch();
  const { notifySuccess, notifyError } = useNotificationProvider();
  const { isAdmin, isChecked } = useInstanceSettingsAccess();

  // Keep the global enablement map in sync after an `enabled` row is mutated,
  // so toggling an app on/off takes effect without a full reload.
  const refreshEnabledApps = useCallback(async () => {
    try {
      const enabledApps = await getEnabledApps();
      dispatch(storeEnabledApps(enabledApps));
    } catch (error) {
      console.error('[useInstanceSettings] Enabled apps refresh failed:', error);
      dispatch(setEnabledAppsError());
    }
  }, [dispatch]);

  const [settings, setSettings] = useState<InstanceSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterApp, setFilterApp] = useState<string>('all');
  const [newSetting, setNewSetting] = useState<NewSettingForm>(EMPTY_FORM);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getInstanceSettings();
      setSettings(response.data ?? []);
    } catch (error) {
      console.error('[useInstanceSettings] Fetch failed:', error);
      notifyError('Failed to load instance settings');
    } finally {
      setIsLoading(false);
    }
  }, [notifyError]);

  useEffect(() => {
    if (isChecked && isAdmin) {
      fetchSettings();
    }
  }, [isChecked, isAdmin, fetchSettings]);

  const onFilterChange = (app: string) => {
    setFilterApp(app);
  };

  const filteredSettings =
    filterApp === 'all'
      ? settings
      : settings.filter((s) => s.app === filterApp);

  const appOptions = Array.from(new Set(settings.map((s) => s.app))).sort();

  const onSaveCell = async (
    app: string,
    key: string,
    newValue: string
  ): Promise<boolean> => {
    try {
      await upsertInstanceSetting(app, key, newValue);
      setSettings((prev) =>
        prev.map((s) =>
          s.app === app && s.key === key ? { ...s, value: newValue } : s
        )
      );
      if (key === 'enabled') {
        refreshEnabledApps();
      }
      notifySuccess('Setting updated');
      return true;
    } catch (error) {
      console.error('[useInstanceSettings] Update failed:', error);
      notifyError('Failed to update setting');
      return false;
    }
  };

  const onDelete = async (app: string, key: string) => {
    try {
      await deleteInstanceSetting(app, key);
      setSettings((prev) =>
        prev.filter((s) => !(s.app === app && s.key === key))
      );
      if (key === 'enabled') {
        refreshEnabledApps();
      }
      notifySuccess('Setting deleted');
    } catch (error) {
      console.error('[useInstanceSettings] Delete failed:', error);
      notifyError('Failed to delete setting');
      throw error;
    }
  };

  const onNewSettingChange = (field: keyof NewSettingForm, value: string) => {
    setNewSetting((prev) => ({ ...prev, [field]: value }));
  };

  const onAddSetting = async () => {
    const { app, key, value } = newSetting;
    if (!app.trim() || !key.trim()) {
      notifyError('App and Key are required');
      return;
    }
    try {
      const response = await upsertInstanceSetting(
        app.trim(),
        key.trim(),
        value
      );
      const created: InstanceSetting = response.data;
      setSettings((prev) => {
        const exists = prev.some(
          (s) => s.app === created.app && s.key === created.key
        );
        if (exists) {
          return prev.map((s) =>
            s.app === created.app && s.key === created.key ? created : s
          );
        }
        return [...prev, created];
      });
      setNewSetting(EMPTY_FORM);
      if (created.key === 'enabled') {
        refreshEnabledApps();
      }
      notifySuccess('Setting added');
    } catch (error) {
      console.error('[useInstanceSettings] Add failed:', error);
      notifyError('Failed to add setting');
      throw error;
    }
  };

  const state = {
    settings: filteredSettings,
    isLoading,
    isAdmin,
    filterApp,
    appOptions,
    newSetting,
  };

  const handler = {
    onFilterChange,
    onSaveCell,
    onDelete,
    onNewSettingChange,
    onAddSetting,
    onRefresh: fetchSettings,
  };

  return { state, handler };
}

export default useInstanceSettings;
